import React, { useState, useEffect } from 'react';
import {
  Select,
  Modal,
  Input,
  Button,
  Spin,
  Tooltip,
  Empty,
  message
} from 'antd';
import { MdAdd } from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { POST, GET } from '../../utils/apis';
import { useSelector } from 'react-redux';
import { translations } from '../../utils/constants';
import { secureApi } from '../../Config/api';

const { Option } = Select;

const CustomDropdown = ({
  language,
  label,
  options: initialOptions = [],
  onOptionChange,
  section,
  dropdownName,
  value,
  placeholderTxt
}) => {
  const user = useSelector((state) => state.authReducer?.user);
  const userId = user?.id;

  const [options, setOptions] = useState([]);
  const [selectedValue, setSelectedValue] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newOptions, setNewOptions] = useState([]);
  const [inputErrors, setInputErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    if (value && options.includes(value)) {
      setSelectedValue(value);
    } else {
      setSelectedValue('');
    }
  }, [value, options]);

  const fetchOptions = async () => {
    setDropdownLoading(true);
    try {
      const response = await secureApi.get(GET?.DROPDOWN_OPTIONS, {
        params: { section, dropdownName, userId },
      });
      const responseData = response?.data || [];
      const fetchedOptions = responseData[0]?.options || [];
      setOptions(fetchedOptions);
    } catch (err) {
      console.error('Error fetching dropdown options:', err);
    } finally {
      setDropdownLoading(false);
    }
  };

  const handleSelectChange = (val) => {
    if (val === 'add-edit') {
      setIsModalOpen(true);
    } else {
      setSelectedValue(val);
      onOptionChange?.(val);
    }
  };

  const handleAddNewOption = () => {
    setNewOptions([...newOptions, '']);
    setInputErrors([...inputErrors, '']);
  };

  const handleNewOptionChange = (index, val) => {
    const updated = [...newOptions];
    updated[index] = val;
    setNewOptions(updated);

    const updatedErrors = [...inputErrors];
    updatedErrors[index] = val.trim() === '' ? 'Field cannot be empty' : '';
    setInputErrors(updatedErrors);
  };

  const handleDeleteOption = async (option, index, isNew = false) => {
    if (isNew) {
      setNewOptions(newOptions.filter((_, i) => i !== index));
      setInputErrors(inputErrors.filter((_, i) => i !== index));
    } else {
      const updatedOptions = options.filter((opt) => opt !== option);
      setOptions(updatedOptions);
      try {
        await secureApi.post(POST?.DELETE_DROPDOWN, {
          section,
          dropdown_name: dropdownName,
          optionToRemove: option,
          user_id: userId,
        });
      } catch (err) {
        console.error('Error deleting option:', err);
      }
    }
  };

  const handleOptionEdit = (index, val) => {
    const updated = [...options];
    updated[index] = val;
    setOptions(updated);
    setIsEditing(true);
  };

  const handleSave = async () => {
    const allInputs = [...options, ...newOptions];
    const errors = allInputs.map((opt) => (opt.trim() === '' ? 'Field cannot be empty' : ''));
    setInputErrors(errors);

    if (errors.some((err) => err)) return;

    setLoading(true);
    try {
      await secureApi.post(POST?.ADD_DROPDOWN, {
        section,
        dropdown_name: dropdownName,
        options: [...new Set(allInputs)],
        user_id: userId,
      });

      setOptions([...new Set(allInputs)]);
      setNewOptions([]);
      setIsModalOpen(false);
      setIsEditing(false);
      setInputErrors([]);
      message.success(translations[language]?.savedSuccessfully || 'Saved');
    } catch (err) {
      console.error('Error saving options:', err);
      message.error(translations[language]?.errorSaving || 'Error saving options');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='custom-dropdown'>
      <Select
        showSearch
        style={{ width: '100%' }}
        value={selectedValue || undefined}
        className="antd-select"
        onChange={handleSelectChange}
        loading={dropdownLoading}
        placeholder={placeholderTxt || "Select an option"}
        dropdownRender={(menu) => (
          <>
            {menu}
            <Button
              type='text'
              style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}
              icon={<MdAdd />}
              onClick={() => setIsModalOpen(true)}
            >
              {translations[language]?.addEdit}
            </Button>
          </>
        )}
      >
        {options?.map((opt) => (
          <Option key={opt} value={opt}>
            <Tooltip title={opt}>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt}</span>
            </Tooltip>
          </Option>
        ))}
      </Select>

      <Modal
        open={isModalOpen}
        title={translations[language]?.dropdownOptions || 'Dropdown Options'}
        onCancel={() => {
          setIsModalOpen(false);
          setNewOptions([]);
          setInputErrors([]);
        }}
        footer={[
          <Button key='cancel' onClick={() => setIsModalOpen(false)}>
            {translations[language]?.cancel || 'Cancel'}
          </Button>,
          <Button key='submit' type='primary' onClick={handleSave} disabled={loading}>
            {loading ? <Spin /> : isEditing ? translations[language]?.updateButton || 'Update' : translations[language]?.saveButton || 'Save'}
          </Button>,
        ]}
      >
        <h3>{dropdownName}</h3>

        {newOptions?.length || options?.length > 0 ? (
          options?.map((opt, index) => (
            <div key={index} style={{ display: 'flex', marginBottom: 11 }}>
              <Input
                className='modal-inpt'
                placeholder='Enter new option'
                value={opt}
                onChange={(e) => handleOptionEdit(index, e.target.value)}
              />
              <Button
                danger
                style={{ marginLeft: '12px' }}
                icon={<RiDeleteBin6Line />}
                onClick={() => handleDeleteOption(opt, index, false)}
              />
            </div>
          ))
        ) : (
          <Empty description={translations[language]?.noOptionsAvailable} />
        )}

        {newOptions.map((opt, index) => (
          <div key={index} style={{ display: 'flex', marginBottom: 11 }}>
            <Input
              className='modal-inpt'
              placeholder='Enter new option'
              value={opt}
              onChange={(e) => handleNewOptionChange(index, e.target.value)}
              status={inputErrors[index] ? 'error' : ''}
            />
            <Button
              danger
              style={{ marginLeft: '12px' }}
              icon={<RiDeleteBin6Line />}
              onClick={() => handleDeleteOption(opt, index, true)}
            />
          </div>
        ))}

        <Button
          icon={<MdAdd />}
          type='dashed'
          block
          style={{ marginTop: '13px' }}
          onClick={handleAddNewOption}
        >
          {translations[language]?.addNewOption}
        </Button>
      </Modal>
    </div>
  );
};

export default CustomDropdown;
